import React from "react";
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandableSection from "../../../mypage/ExpandableSection";

const weekdayLabels = ["月", "火", "水", "木", "金", "土", "日"];

function buildDeliverySummary({ weekdays, deliveryTime }) {
  if (weekdays.length === 0) return "曜日未設定";
  if (weekdays.length === 7) return `毎日 ${deliveryTime}`;

  const weekdayText = weekdays.map((n) => weekdayLabels[n] ?? "?").join("・");

  return `毎週 ${weekdayText}曜 ${deliveryTime}`;
}

export function DeliverySettingCard({
  title,
  weekdays,
  hour,
  minute,
  active,
  disabled = false,
  inputsDisabled = false,
  hideScheduleInputs = false,
  onWeekdaysChange,
  onHourChange,
  onMinuteChange,
  onActiveChange,
}) {
  // ON/OFFのみ管理する配信は曜日・時刻設定を表示しない
  if (hideScheduleInputs) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography fontWeight={700}>{title}</Typography>

            <Switch
              checked={active}
              onChange={(e) => onActiveChange(e.target.checked)}
              disabled={disabled}
              size="small"
              aria-label={`${title}の配信ON/OFF`}
            />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const handleWeekdayToggle = (index) => {
    const next = weekdays.includes(index)
      ? weekdays.filter((d) => d !== index)
      : [...weekdays, index].sort((a, b) => a - b);
    onWeekdaysChange(next);
  };

  const expandedContent = (
    <Box>
      <Divider sx={{ my: 2, borderStyle: "dashed" }} />

      <Stack direction="row" alignItems="center" spacing={3}>
        <Typography variant="body2" sx={{ minWidth: 56 }}>
          配信曜日
        </Typography>
        <Stack direction="row" spacing={0.5}>
          {weekdayLabels.map((label, index) => {
            const isSelected = weekdays.includes(index);
            return (
              <ButtonBase
                key={index}
                onClick={() => handleWeekdayToggle(index)}
                aria-pressed={isSelected}
                aria-label={`${label}曜日`}
                disabled={inputsDisabled}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  border: "1px solid",
                  bgcolor: isSelected ? "primary.main" : "transparent",
                  color: isSelected ? "white" : "text.primary",
                  borderColor: isSelected ? "primary.main" : "divider",
                  fontSize: "0.875rem",
                  fontWeight: isSelected ? 700 : 400,
                  transition: "background-color 0.15s, color 0.15s",
                }}
              >
                {label}
              </ButtonBase>
            );
          })}
        </Stack>
      </Stack>

      <Divider sx={{ my: 2, borderStyle: "dashed" }} />

      <Stack direction="row" alignItems="center" spacing={3}>
        <Typography variant="body2" sx={{ minWidth: 56 }}>
          配信時刻
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Select
            value={hour}
            onChange={(e) => onHourChange(e.target.value)}
            size="small"
            inputProps={{ "aria-label": "配信時刻（時）" }}
            disabled={inputsDisabled}
            sx={{ width: 80 }}
          >
            {Array.from({ length: 24 }, (_, i) =>
              String(i).padStart(2, "0"),
            ).map((h) => (
              <MenuItem key={h} value={h}>
                {h}
              </MenuItem>
            ))}
          </Select>
          <Typography>:</Typography>
          <Select
            value={minute}
            onChange={(e) => onMinuteChange(e.target.value)}
            size="small"
            inputProps={{ "aria-label": "配信時刻（分）" }}
            disabled={inputsDisabled}
            sx={{ width: 80 }}
          >
            {["00", "15", "30", "45"].map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Stack>
    </Box>
  );

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <ExpandableSection
          renderButton={({ toggle, expanded }) => (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography fontWeight={700}>{title}</Typography>

                <Typography variant="body2" color="text.secondary">
                  {buildDeliverySummary({
                    weekdays,
                    deliveryTime: `${hour}:${minute}`,
                  })}
                </Typography>
              </Box>

              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Switch
                  checked={active}
                  onChange={(e) => onActiveChange(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  disabled={disabled}
                  size="small"
                  aria-label={`${title}の配信ON/OFF`}
                />
                <IconButton
                  onClick={toggle}
                  size="small"
                  aria-label={expanded ? "配信設定を閉じる" : "配信設定を開く"}
                  aria-expanded={expanded}
                >
                  <ExpandMoreIcon
                    sx={{
                      transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "0.2s",
                    }}
                  />
                </IconButton>
              </Stack>
            </Stack>
          )}
          expandedContent={expandedContent}
        />
      </CardContent>
    </Card>
  );
}
